<template>
    <div
        class="d-flex gap-3 w-100"
        :class="
            localField.type == 'divider'
                ? 'align-items-center'
                : 'align-items-start'
        "
        ref="wrapper"
    >
        <div class="flex-grow-1 min-w-0">
            <!-- Divider -->
            <div v-if="localField.type == 'divider'">
                <hr class="divider-line" />
            </div>
            <!-- Fields -->
            <div v-else-if="isField" class="d-flex flex-column gap-2">
                <!-- Field Label -->
                <div
                    v-if="
                        isBuilding &&
                        localField.isEditing &&
                        localField.type !== 'checkbox'
                    "
                    class="d-flex flex-wrap gap-2 align-items-start"
                >
                    <Field
                        class="flex-grow-1"
                        v-model="localField.label"
                        type="text"
                        :required="true"
                        :params="{ placeholder: 'Enter Question...' }"
                        ref="fieldLabel"
                        @keydown.enter.prevent="toggleEdit"
                    ></Field>
                    <button
                        v-if="localField.type == 'radio'"
                        title="Toggle layout"
                        class="btn btn-sm btn-white ms-auto"
                        style="border-radius: var(--radius-default)"
                        @click="toggleOrientation"
                    >
                        <span>
                            {{
                                localField.params.stacked
                                    ? 'Stacked'
                                    : 'Wrapped'
                            }}
                        </span>
                    </button>
                </div>

                <!-- Upload Field -->
                <div
                    v-if="localField.type == 'file-upload'"
                    class="d-flex flex-column gap-2"
                >
                    <div
                        v-if="!localField.isEditing"
                        class="d-flex align-items-baseline"
                    >
                        <span class="element-label">
                            {{ localField.label }}
                        </span>
                        <span v-if="localField.required">*</span>
                    </div>
                    <div class="file-card">
                        <AdvancedUploadField
                            class="w-100"
                            accept="image"
                            :multiple="true"
                            :max-image-height="3000"
                            :max-image-width="3000"
                            v-model="localField.value"
                            :required="localField.required"
                            :hide-upload-field="disabled"
                            :hide-delete-button="disabled"
                            :disabled="isBuilding"
                        />
                    </div>
                </div>
                <!-- Other Fields -->
                <div
                    v-else-if="
                        !localField.params?.options || !localField.isEditing
                    "
                    class="d-flex align-items-center w-100 gap-2"
                >
                    <Field
                        :class="localField.type !== 'checkbox' ? 'w-100' : ''"
                        v-model="localField.value"
                        :type="localField.type"
                        :label="
                            !localField.isEditing
                                ? localField.label
                                : localField.type !== 'checkbox'
                                ? 'Default Value'
                                : ''
                        "
                        :isEditing="localField.isEditing"
                        :required="localField.required"
                        :disabled="
                            disabled || (isBuilding && !localField.isEditing)
                        "
                        :params="scopedParams"
                        @create-option="createOption"
                        @remove-option="removeOption"
                        @keydown.enter.prevent="toggleEdit"
                    />
                    <!-- Checkbox Label -->
                    <Field
                        v-if="
                            isBuilding &&
                            localField.isEditing &&
                            localField.type == 'checkbox'
                        "
                        v-model="localField.label"
                        type="text"
                        :required="true"
                        :params="{ placeholder: 'Enter Question...' }"
                        ref="fieldLabel"
                        @keydown.enter.prevent="toggleEdit"
                        class="w-100"
                    ></Field>

                    <Timer
                        v-if="localField.type == 'radio' && isTimer"
                        ref="timerComponent"
                        v-model="localField.timer"
                        :disabled="disabled || isBuilding"
                        @timer-started="handleTimerRunning(true)"
                        @timer-stopped="handleTimerRunning(false)"
                        @timer-reset="handleTimerRunning(false)"
                    ></Timer>
                </div>

                <div
                    v-if="localField.params?.options && localField.isEditing"
                    class="d-flex flex-column gap-2"
                >
                    <div
                        v-for="(option, index) in localField.params.options"
                        :key="index"
                        class="d-flex gap-2 align-items-center"
                    >
                        <button
                            v-if="isScoring"
                            class="icon-btn icon-btn-xs"
                            :title="option.isCorrect ? 'Correct' : 'Incorrect'"
                            @click="handleScoringRuleToggle(option.value)"
                            :style="
                                option.isCorrect
                                    ? 'background-color: var(--success); color: white;'
                                    : 'background-color: var(--danger); color: white;'
                            "
                        >
                            <i
                                class="fi"
                                :class="option.isCorrect ? 'fi-check' : 'fi-x'"
                            ></i>
                        </button>
                        <!-- Editable label input -->
                        <Field
                            class="w-100 ms-1"
                            v-model="option.label"
                            type="text"
                            :required="true"
                            :ref="'optionLabel-' + index"
                            :params="{ placeholder: 'Enter Option...' }"
                            @keydown.enter.prevent="toggleEdit"
                        ></Field>

                        <!-- Remove option button -->
                        <button
                            v-if="index > 1"
                            class="icon-btn icon-btn-light"
                            title="Remove option"
                            @click="removeOption($event, option)"
                        >
                            <i class="fi fi-trash"></i>
                        </button>
                    </div>
                </div>
                <!-- Add Option + Scoring Controls -->
                <div
                    v-if="localField.isEditing"
                    class="d-flex flex-column gap-2 w-100"
                >
                    <button
                        v-if="localField.params?.options"
                        class="btn btn-white"
                        @click="handleAddOption"
                        style="width: fit-content"
                    >
                        Add Option
                        <i class="ml-1 fi fi-add add-option-icon"></i>
                    </button>
                    <div
                        class="d-flex flex-wrap align-items-start w-100 pt-2 border-top"
                        style="row-gap: 0.5rem; column-gap: 1.5rem"
                    >
                        <div
                            v-if="isScoring"
                            class="d-flex flex-column align-items-center gap-2 me-auto"
                        >
                            <div class="d-flex w-100 align-items-center gap-2">
                                <label class="element-label">Points:</label>
                                <Field
                                    v-model="localField.max_score"
                                    type="number"
                                    :params="{
                                        min: 1,
                                        max: 100,
                                    }"
                                    style="width: 68px"
                                ></Field>
                                <ToolTip>
                                    <template #tooltip-trigger>
                                        <i
                                            class="fi fi-info"
                                            style="color: var(--primary-400)"
                                        ></i>
                                    </template>

                                    <template #tooltip-content>
                                        <div style="width: 143px">
                                            Only one of the correct answers
                                            needs to be chosen to receive full
                                            points.
                                        </div>
                                    </template>
                                </ToolTip>
                            </div>
                            <div
                                v-if="isTimer"
                                class="d-flex w-100 align-items-center gap-2"
                            >
                                <label class="element-label"
                                    >Timer Threshold:</label
                                >
                                <Field
                                    v-model="localField.threshold"
                                    type="number"
                                    :params="{
                                        min: 0,
                                        max: 10000,
                                    }"
                                    style="width: 68px"
                                ></Field>
                                <ToolTip>
                                    <template #tooltip-trigger>
                                        <i
                                            class="fi fi-info"
                                            style="color: var(--primary-400)"
                                        ></i>
                                    </template>

                                    <template #tooltip-content>
                                        <div style="width: 143px">
                                            The amount of time needed in seconds
                                            to receive full points.
                                        </div>
                                    </template>
                                </ToolTip>
                            </div>
                        </div>
                        <div
                            class="d-flex flex-wrap align-items-start"
                            style="row-gap: 0.5rem; column-gap: 1.5rem"
                        >
                            <Field
                                v-if="localField.type !== 'checkbox'"
                                label="Required?"
                                type="boolean"
                                v-model="localField.required"
                            ></Field>
                            <Field
                                v-if="localField.params?.options"
                                label="Score?"
                                type="boolean"
                                v-model="isScoring"
                                @update:model-value="handleScoringToggle"
                            ></Field>
                            <Field
                                v-if="localField.type == 'radio'"
                                label="Timer?"
                                type="boolean"
                                v-model="isTimer"
                                @update:model-value="handleTimerToggle"
                            ></Field>
                        </div>
                    </div>

                    <Field
                        v-if="
                            localField.type == 'checkbox' &&
                            localField.isEditing
                        "
                        v-model="hasConditionals"
                        label="Show notes if unchecked"
                        type="boolean"
                        @update:model-value="toggleConditional"
                    ></Field>
                </div>
                <Field
                    v-if="
                        localField.conditionals?.[0] &&
                        (localField.isEditing ||
                            localField.value ==
                                localField.conditionals?.[0].value)
                    "
                    v-model="localField.conditionals[0].field.value"
                    :label="localField.conditionals[0].field.label"
                    :type="localField.conditionals[0].field.type"
                    :disabled="disabled"
                ></Field>
            </div>
            <!-- Content -->
            <!-- h3 -->
            <div
                v-else-if="localField.type == 'h3'"
                class="d-flex align-items-center gap-2"
            >
                <h3 v-if="!localField.isEditing">{{ localField.label }}</h3>
                <textarea
                    v-else
                    v-model="localField.label"
                    :required="true"
                    placeholder="Enter Heading..."
                    ref="fieldLabel"
                    @keydown.enter.prevent="toggleEdit"
                    class="editable-textarea form-heading"
                    rows="1"
                ></textarea>
            </div>
            <!-- Paragraph -->
            <div
                v-else-if="localField.type == 'p'"
                class="d-flex align-items-center gap-2"
            >
                <p class="text-muted m-0" v-if="!localField.isEditing">
                    {{ localField.label }}
                </p>
                <textarea
                    v-else
                    v-model="localField.label"
                    :required="true"
                    placeholder="Enter Paragraph..."
                    ref="fieldLabel"
                    @keydown.enter.prevent="toggleEdit"
                    class="editable-textarea text-muted"
                    rows="1"
                ></textarea>
            </div>
            <!-- Add a URL -->
            <div
                v-else-if="localField.type == 'url'"
                class="d-flex flex-column justify-content-center gap-2"
            >
                <div class="d-flex align-items-center gap-2">
                    <a :href="localField.value" target="_blank">
                        {{ localField.label || 'Open Link' }}
                    </a>
                </div>
                <Field
                    v-if="localField.isEditing"
                    class="w-100"
                    v-model="localField.label"
                    label="Display Text"
                    :type="localField.type"
                    :params="{ placeholder: 'Enter value' }"
                />
                <Field
                    v-if="localField.isEditing"
                    class="w-100"
                    v-model="localField.value"
                    label="Link"
                    :type="localField.type"
                    :params="{ placeholder: 'Enter value' }"
                />
            </div>
            <!-- Upload an image -->
            <div
                v-else-if="
                    localField.type == 'upload-image' &&
                    (isBuilding || localField.value)
                "
                class="d-flex justify-content-center align-items-center gap-2"
            >
                <div class="d-flex flex-column gap-2 w-100">
                    <div class="d-flex">
                        <span v-if="isBuilding" class="element-label">
                            Upload an Image
                        </span>
                    </div>
                    <div
                        v-if="localField.isEditing || !localField.value"
                        class="d-flex flex-column gap-2"
                    >
                        <UploadField
                            class="w-100"
                            accept="image"
                            max-image-height="800"
                            max-image-width="400"
                            @uploaded="saveFile"
                        />
                    </div>
                    <ImageExpandable
                        v-else
                        :image-id="localField.value"
                        :no-thumb="true"
                    />
                </div>
            </div>
            <!-- Upload a file -->
            <div
                v-else-if="localField.type == 'upload-file'"
                class="d-flex justify-content-center align-items-center gap-2"
            >
                <div class="d-flex flex-column gap-2 w-100">
                    <div class="d-flex">
                        <span v-if="isBuilding" class="element-label">
                            Upload a File
                        </span>
                    </div>
                    <div class="file-card">
                        <AdvancedUploadField
                            class="w-100"
                            :multiple="true"
                            v-model="localField.value"
                            :hide-upload-field="
                                (!localField.isEditing &&
                                    localField.value[0]) ||
                                !isBuilding
                            "
                            :hide-delete-button="!isBuilding"
                            @update:modelValue="toggleEdit"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex flex-column align-items-end gap-2">
            <button
                v-if="showEdit"
                :title="
                    localField.isEditing ? 'Finish editing' : 'Edit element'
                "
                class="icon-btn icon-btn-sm icon-btn-white"
                @click="toggleEdit"
            >
                <i
                    class="fi"
                    :class="localField.isEditing ? 'fi-check' : 'fi-edit'"
                ></i>
            </button>
            <button
                v-if="isBuilding"
                title="Remove element"
                @click="$emit('remove', localField)"
                class="icon-btn icon-btn-sm icon-btn-light"
            >
                <i class="fi fi-trash"></i>
            </button>
        </div>
    </div>
</template>

<script>
import Field from './Field.vue';
import AdvancedUploadField from './fields/AdvancedUploadField.vue';
import Timer from './fields/Timer.vue';
import ToolTip from './fields/ToolTip.vue';
import UploadField from './fields/UploadField.vue';
import ImageExpandable from './ImageExpandable.vue';

// types representing fillable fields
const FIELD_TYPES = [
    'text',
    'textarea',
    'date',
    'select',
    'multi-select',
    'checkbox',
    'checkbox-group',
    'range',
    'radio',
    'file-upload',
];

export default {
    name: 'FieldBuilder',
    components: {
        Field,
        UploadField,
        AdvancedUploadField,
        ImageExpandable,
        ToolTip,
        Timer,
    },
    props: {
        field: {
            type: Object,
            required: true,
        },
        // Activates builder mode so the element can be altered (changing fields properties, heading text, etc.)
        isBuilding: {
            type: Boolean,
            default: false,
        },
        // If true, disables all fields (for view-only)
        disabled: {
            type: Boolean,
            default: false,
        },
        // Panel ID to scope field names for fields in multi-panel scenarios
        panelId: {
            type: [Number, String],
            default: null,
        },
    },
    emits: ['update:field', 'remove'],
    data() {
        return {
            localField: null,
            isScoring: false,
            isTimer: false,
            hasConditionals: false,
            previousTimer: null, // stash for restoring
            previousThreshold: null, // stash for restoring
            timerRunning: false, // track timer state
        };
    },
    watch: {
        // When parent prop changes, sync into local copy
        field: {
            handler(newField) {
                // Only update if the prop changes, preventing unnecessary mutation
                if (
                    JSON.stringify(newField) !== JSON.stringify(this.localField)
                ) {
                    this.localField = { ...newField };
                }
            },
            deep: true,
            immediate: true,
        },
        isValid(val) {
            this.localField.isValid = val;
        },
        // When local copy mutates, emit update
        localField: {
            handler(updated) {
                // Only emit update if there are actual changes (deep comparison)
                if (JSON.stringify(updated) !== JSON.stringify(this.field)) {
                    this.$emit('update:field', updated);
                }
            },
            deep: true,
        },

        // Watch for field value changes to auto-start timer
        'localField.value': {
            handler(newValue, oldValue) {
                // Only auto-start timer if not in building mode and timer is enabled
                if (!this.isBuilding && this.isTimer && newValue !== oldValue) {
                    // Check if the selected value matches any correct scoring rule
                    const isCorrectAnswer =
                        this.localField.scoring_rules?.includes(newValue);

                    if (isCorrectAnswer && !this.timerRunning) {
                        this.startTimer();
                    }
                }
            },
            deep: true,
        },
    },
    computed: {
        isField() {
            return FIELD_TYPES.includes(this.localField?.type);
        },
        showEdit() {
            if (this.localField.type == 'divider') {
                return false;
            }
            return this.isBuilding;
        },
        scopedParams() {
            // Scope field names with panelId for radio/checkbox-group fields to prevent interference between panels
            if (this.panelId == null || !this.localField?.params) {
                return this.localField?.params;
            }

            const fieldType = this.localField.type;
            if (fieldType === 'radio' || fieldType === 'checkbox-group') {
                const params = { ...this.localField.params };
                // Use existing name if provided, otherwise slugify the label (matching Field.vue behavior)
                const originalName =
                    params.name ||
                    (this.localField.label
                        ? this.localField.label
                              .replaceAll(' ', '-')
                              .toLowerCase()
                        : this.localField.id || `field-${Date.now()}`);
                // Add panelId prefix to field name
                params.name = `panel-${this.panelId}-${
                    originalName || `field-${Date.now()}`
                }`;
                return params;
            }

            return this.localField.params;
        },
        isValid() {
            if (this.isField) {
                // check label
                const trimmedLabel = this.localField.label?.trim();
                if (!trimmedLabel) return false;

                // check options if present
                if (this.localField.params?.options?.length) {
                    return this.localField.params.options.every((o) =>
                        o.label?.trim()
                    );
                }
                // check file upload
            } else if (this.localField.type === 'upload-file') {
                // require at least one file
                if (
                    !Array.isArray(this.localField.value) ||
                    this.localField.value.length === 0
                ) {
                    return false;
                }
                // check image upload
            } else if (this.localField.type === 'upload-image') {
                if (!this.localField.value) return false;
            } else if (
                this.localField.type === 'h3' ||
                this.localField.type === 'p'
            ) {
                if (!this.localField.label) return false;
            }
            return true;
        },
    },
    methods: {
        toggleEdit() {
            if (this.localField.isEditing) {
                if (!this.isValid) {
                    // validation failed → focus on first invalid field
                    if (!this.localField.label?.trim()) {
                        this.$refs.fieldLabel?.focus?.();
                    } else {
                        // find first invalid option
                        const idx = this.localField.params?.options?.findIndex(
                            (o) => !o.label?.trim()
                        );
                        if (idx >= 0)
                            this.$refs[`optionLabel-${idx}`]?.focus?.();
                    }
                    return;
                }
            }

            // toggle normally if valid
            if (this.isBuilding) {
                this.localField.isEditing = !this.localField.isEditing;
            } else {
                this.localField.isEditing = false;
            }

            if (this.localField.isEditing) {
                this.$nextTick(() => {
                    this.$refs.fieldLabel?.focus?.();
                });
            }
        },
        toggleOrientation() {
            this.localField.params.stacked = !this.localField.params.stacked;
        },
        generateUniqueValue() {
            return Math.random().toString(36).substring(2, 10); // 8 characters, e.g., "a93kfj2d"
        },
        createOption(newOptionValue) {
            this.localField.params.options.push({
                label: newOptionValue,
                value: newOptionValue,
            });
        },
        removeOption(event, optionToRemove) {
            event.stopPropagation();
            this.localField.params.options =
                this.localField.params.options.filter(
                    (opt) => opt.value !== optionToRemove.value
                );
        },
        handleAddOption() {
            const newIndex = this.localField.params.options.length + 1;
            const newOption = {
                label: `Option ${newIndex}`,
                value: this.generateUniqueValue(),
                isCorrect: false,
            };
            this.localField.params.options.push(newOption);
        },
        toggleConditional() {
            const conditionalNotes = {
                value: false,
                field: {
                    id: Date.now(),
                    type: 'textarea',
                    label: 'Notes',
                    value: null,
                },
            };

            if (this.hasConditionals) {
                this.localField.conditionals = [conditionalNotes];
            } else {
                this.localField.conditionals = [];
            }
        },
        handleTimerToggle(value) {
            if (value) {
                // Restore previous values if we have them, otherwise set defaults
                this.localField.timer = this.previousTimer ?? 0;
                this.localField.threshold = this.previousThreshold ?? 0;
                this.timerRunning = false; // Reset timer state when re-enabling
            } else {
                // Stash current values in case user re-enables later
                this.previousTimer ??= this.localField.timer;
                this.previousThreshold ??= this.localField.threshold;

                // Explicitly null out for backend
                this.localField.timer = null;
                this.localField.threshold = null;
                this.timerRunning = false; // Stop timer when disabling
            }
        },
        handleScoringToggle(value) {
            if (value) {
                this.localField.max_score = 1;
                if (!this.localField.scoring_rules) {
                    this.localField.scoring_rules = [];
                }
            } else {
                delete this.localField.max_score;
                if (this.localField.scoring_rules) {
                    delete this.localField.scoring_rules;
                }
                if (
                    this.localField.params &&
                    Array.isArray(this.localField.params.options)
                ) {
                    this.localField.params.options.forEach((option) => {
                        if ('isCorrect' in option) {
                            delete option.isCorrect;
                        }
                    });
                }
            }
        },
        handleScoringRuleToggle(value) {
            const option = this.localField.params.options.find(
                (opt) => opt.value === value
            );

            if (!option) return;

            option.isCorrect = !option.isCorrect;

            if (option.isCorrect) {
                if (!this.localField.scoring_rules.includes(value)) {
                    this.localField.scoring_rules.push(value);
                }
            } else {
                this.localField.scoring_rules =
                    this.localField.scoring_rules.filter(
                        (val) => val !== value
                    );
            }
        },
        saveFile(response, file) {
            this.localField.value = response.data.id;
            this.localField.isEditing = false;
        },
        handleClickOutside(event) {
            const clickedInside = this.$refs.wrapper.contains(event.target);
            if (!clickedInside && this.localField.isEditing) {
                this.toggleEdit();
                this.$emit('update:field', this.localField);
            }
        },
        startTimer() {
            if (this.$refs.timerComponent && !this.timerRunning) {
                // Create a mock event object to prevent errors
                const mockEvent = {
                    stopPropagation: () => {},
                };
                this.$refs.timerComponent.toggleTimer(mockEvent);
                this.timerRunning = true;
            }
        },
        handleTimerRunning(isRunning) {
            this.timerRunning = isRunning;
        },
    },
    created() {
        this.localField = { ...this.field };
        this.isScoring = !!this.field.max_score;

        // Ensure legacy data with scoring has isCorrect defined
        if (this.isScoring && this.localField.params?.options) {
            this.localField.params.options.forEach((option) => {
                if (typeof option.isCorrect === 'undefined') {
                    option.isCorrect = false;
                }
            });
        }
        this.hasConditionals =
            Array.isArray(this.field.conditionals) &&
            this.field.conditionals.length > 0;

        // Determine if timer is active
        if (typeof this.field.timer === 'number') {
            this.isTimer = true;
            this.previousTimer = this.field.timer;
            this.previousThreshold = this.field.threshold ?? 0;
        } else {
            this.isTimer = false;
        }
    },
    mounted() {
        this.$nextTick(() => {
            const el = this.$refs['fieldLabel'];
            if (el) {
                el.focus();
            }
        });
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    },
};
</script>

<style scoped>
.divider-line {
    border: none;
    border-top: 1px solid var(--neutral-700);
    margin: 0;
    width: 100%;
}

.element-label {
    color: var(--Colours-Neutral-900, #474747);
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 100%;
}

.checkbox-label {
    color: var(--neutral-800);
    font-size: 0.875rem;
    font-weight: 400 !important;
    font-weight: 600;
    line-height: 100%;
}

.editable-textarea {
    width: 100%;
    min-height: 0.875rem;
    /* sets the one-line height to match the static label */
    resize: none;
    border: none;
    padding: 0;
    margin: 0;
    outline: none;
    font-weight: 550;
    line-height: 100%;
    background: transparent;
    overflow: hidden;
}

.form-heading {
    font-size: 1.125rem;
    line-height: var(--space-6);
    font-family: Montserrat, sans-serif;
}

.icon-btn-xs {
    display: flex;
    width: 1.375rem;
    height: 1.375rem;
    padding: 0.4375rem 0.625rem;
    justify-content: center;
    align-items: center;
    gap: 0.625rem;
    border-radius: var(--Radius-radius-sm, 0.25rem);
    font-size: 0.875rem;
}

.close-icon {
    color: var(--neutral-800);
    transition: transform 0.15s ease, color 0.15s ease;
}

.close-icon:hover {
    color: var(--neutral-900);
    /* slightly darker for emphasis */
}

.range-container {
    margin-top: 1.5rem;
    position: relative;
    width: 100%;
}

.score-display {
    position: absolute;
    top: -1.5rem;
    right: 0;
    font-size: 0.875rem;
    line-height: 0.875rem;
    padding: 0 0.75rem;
}

.file-card {
    width: 100%;
    padding: var(--space-3);
    gap: 0.75rem;
    background-color: var(--neutral-400);
    border-radius: var(--space-3);
}

.min-w-0 {
    min-width: 0;
}

.add-option-icon {
    color: var(--primary-500);
}

.btn:hover .add-option-icon {
    color: white;
}
</style>
